import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Activity } from 'lucide-react'
import { publishingApiClient } from '../../api/http/publishingApiClient'
import type { WireArticle } from '../../api/types'
import type { WorkflowHistoryEntry } from '../metrics/types'

// ── Types ─────────────────────────────────────────────────────────────────────

type ActivityEvent = WorkflowHistoryEntry & {
  articleId: string
  articleTitle: string
}

// ── Stage display maps ────────────────────────────────────────────────────────

const STAGE_LABEL: Record<string, string> = {
  qc_review:            'QC Review',
  layout_review:        'Layout',
  editorial_approval:   'Editorial',
  scheduled:            'Scheduled',
  post_pub_review:      'Post-Pub',
  published:            'Published',
  recalled:             'Recalled',
  archived:             'Archived',
  blocked:              'Blocked',
}

// Inline style colors — Radix step 9 solids read from CSS vars at runtime
// so they work in both light and dark mode without Tailwind class generation.
const STAGE_COLOR: Record<string, string> = {
  qc_review:            'var(--blue-9)',
  layout_review:        'var(--violet-9)',
  editorial_approval:   'var(--amber-9)',
  scheduled:            'var(--jade-9)',
  post_pub_review:      'var(--orange-9)',
  published:            'var(--jade-9)',
  recalled:             'var(--red-9)',
  archived:             'var(--gray-8)',
  blocked:              'var(--red-9)',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1)   return 'just now'
  if (mins < 60)  return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n) + '…' : s
}

// ── Data hook ─────────────────────────────────────────────────────────────────

/** Max articles to fan out across — avoids flooding with requests */
const MAX_ARTICLES = 20

function useRecentActivity(articles: WireArticle[]) {
  // Cap to first N so we don't fire 75+ concurrent requests.
  // The API returns newest-first by default, so this captures the most active ones.
  const capped = articles.slice(0, MAX_ARTICLES)
  const stableKey = capped.map(a => a.id).join(',')

  return useQuery<ActivityEvent[]>({
    queryKey: ['dashboard', 'activity-feed', stableKey],
    queryFn: async () => {
      const results = await Promise.allSettled(
        capped.map(async (a) => {
          const { data } = await publishingApiClient.get<WorkflowHistoryEntry[]>(
            `/drafts/${a.id}/workflow-history`
          )
          return data.map(e => ({ ...e, articleId: a.id, articleTitle: a.title }))
        })
      )
      return results
        .filter((r): r is PromiseFulfilledResult<ActivityEvent[]> => r.status === 'fulfilled')
        .flatMap(r => r.value)
        // Use Date comparison — handles both ISO strings and epoch-ms numbers
        // (Java Timestamp serializes as a number by default in Jackson).
        .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
        .slice(0, 25)
    },
    staleTime: 60_000,
    enabled: articles.length > 0,
  })
}

// ── Component ─────────────────────────────────────────────────────────────────

interface RecentActivityFeedProps {
  /** In-progress articles to fan out history requests across. */
  articles: WireArticle[]
  embedded?: boolean
}

export function RecentActivityFeed({ articles, embedded = false }: RecentActivityFeedProps) {
  const { data: events, isPending } = useRecentActivity(articles)

  const body = (
    <div className="max-h-80 overflow-y-auto divide-y divide-line-subtle">
      {isPending && articles.length > 0 && (
        <>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-3">
              <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-fill" />
              <div className="h-3 flex-1 animate-pulse rounded bg-fill" style={{ maxWidth: `${55 + (i % 3) * 15}%` }} />
              <div className="h-3 w-12 animate-pulse rounded bg-fill" />
            </div>
          ))}
        </>
      )}

      {!isPending && (!events || events.length === 0) && (
        <div className="flex items-center justify-center py-10">
          <p className="font-sans text-sm text-fg-secondary">No recent activity</p>
        </div>
      )}

      {!isPending && events && events.map((ev) => (
        <Link
          key={`${ev.articleId}-${ev.id}`}
          to={`/admin/articles/${ev.articleId}`}
          className="flex items-baseline gap-3 px-5 py-2.5 hover:bg-fill"
        >
          <span
            className="relative top-[3px] h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: STAGE_COLOR[ev.stage] ?? 'var(--gray-8)' }}
          />

          <span className="min-w-0 flex-1 font-sans text-xs text-fg-secondary">
            <span className="text-fg">{truncate(ev.articleTitle, 48)}</span>
            {' → '}
            <span className="font-semibold text-fg">
              {STAGE_LABEL[ev.stage] ?? ev.stage}
            </span>
            {(ev.changedByName ?? ev.changedBy) && (
              <span className="text-fg-secondary">
                {' by '}{ev.changedByName ?? ev.changedBy}
              </span>
            )}
          </span>

          <span className="shrink-0 font-sans text-[11px] tabular-nums text-fg-secondary">
            {timeAgo(ev.changedAt)}
          </span>
        </Link>
      ))}
    </div>
  )

  if (embedded) {
    return <div>{body}</div>
  }

  return (
    <section className="flex flex-col rounded border border-line bg-surface">

      {/* Header */}
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Activity size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
          Recent Activity
        </span>
        {events && events.length > 0 && (
          <span className="ml-auto font-sans text-[10px] text-fg-secondary">
            {events.length} events
          </span>
        )}
      </div>

      {/* Body */}
      {body}
    </section>
  )
}
